import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  endOfMonth,
  endOfYear,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from "date-fns";
import type { DateRange } from "react-day-picker";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";

interface MonthGridPickerProps {
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  initialYear?: number;
}

function buildMonth(year: number, monthIndex: number): Date {
  return new Date(year, monthIndex, 1);
}

function buildLocalizedMonthLabels(locale: string): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { month: "short" });
  return Array.from({ length: 12 }, (_, i) => {
    const label = formatter.format(new Date(2024, i, 1));
    // Remove trailing dot (some locales like pt-BR add it: "Jan.")
    const clean = label.replace(/\.$/, "");
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  });
}

function getMonthState(
  cell: Date,
  range: DateRange | undefined
): "start" | "end" | "middle" | "single" | null {
  if (!range?.from) return null;
  const fromMonth = startOfMonth(range.from);
  const toMonth = range.to ? startOfMonth(range.to) : null;
  const cellMonth = startOfMonth(cell);

  if (!toMonth) {
    return isSameMonth(cellMonth, fromMonth) ? "single" : null;
  }
  if (isSameMonth(cellMonth, fromMonth) && isSameMonth(cellMonth, toMonth))
    return "single";
  if (isSameMonth(cellMonth, fromMonth)) return "start";
  if (isSameMonth(cellMonth, toMonth)) return "end";
  if (cellMonth > fromMonth && cellMonth < toMonth) return "middle";
  return null;
}

export function MonthGridPicker({
  selected,
  onSelect,
  initialYear,
}: MonthGridPickerProps) {
  const { t, i18n } = useTranslation("date-range-picker");
  const [year, setYear] = useState<number>(
    () => initialYear ?? selected?.from?.getFullYear() ?? new Date().getFullYear()
  );

  const monthLabels = useMemo(
    () => buildLocalizedMonthLabels(i18n.language || "pt-BR"),
    [i18n.language]
  );

  const handleClick = (monthIndex: number) => {
    const cell = buildMonth(year, monthIndex);
    const cellStart = startOfMonth(cell);
    const cellEnd = endOfMonth(cell);

    if (!selected?.from || (selected.from && selected.to)) {
      onSelect({ from: cellStart, to: cellEnd });
      return;
    }

    if (cellStart < startOfMonth(selected.from)) {
      onSelect({ from: cellStart, to: endOfMonth(selected.from) });
    } else {
      onSelect({ from: startOfMonth(selected.from), to: cellEnd });
    }
  };

  const handleSelectFullYear = () => {
    const ref = new Date(year, 0, 1);
    onSelect({ from: startOfYear(ref), to: endOfYear(ref) });
  };

  return (
    <div className="select-none w-full max-w-[320px]">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          className="relative h-9 w-9 grid place-items-center rounded-md text-white/70 hover:bg-white/[0.08] hover:text-white before:absolute before:inset-[-4px] before:content-['']"
          aria-label={t("monthGrid.previousYear")}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-white/90" aria-live="polite">
          {year}
        </span>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          className="relative h-9 w-9 grid place-items-center rounded-md text-white/70 hover:bg-white/[0.08] hover:text-white before:absolute before:inset-[-4px] before:content-['']"
          aria-label={t("monthGrid.nextYear")}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1">
        {monthLabels.map((label, idx) => {
          const cell = buildMonth(year, idx);
          const state = getMonthState(cell, selected);
          return (
            <button
              key={idx}
              type="button"
              onClick={() => handleClick(idx)}
              aria-pressed={state !== null}
              className={cn(
                "h-11 rounded-md text-sm font-medium transition-colors text-white/80",
                "hover:bg-white/[0.06] hover:text-white",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/60",
                state === "start" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-r-none",
                state === "end" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground rounded-l-none",
                state === "middle" &&
                  "bg-white/[0.06] text-white hover:bg-white/[0.08] rounded-none",
                state === "single" &&
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleSelectFullYear}
        className="mt-3 w-full text-xs rounded-md py-1.5 bg-white/[0.03] hover:bg-white/[0.06] text-white/60 hover:text-white/90 transition-colors"
      >
        {t("monthGrid.fullYear", { year })}
      </button>
    </div>
  );
}
